import clsx from 'clsx';
import { ChevronDown, Search, type LucideIcon } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ISelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ISearchableSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: ISelectOption[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  maxHeight?: number;
  icon?: LucideIcon;
  searchIcon?: LucideIcon;
  error?: boolean;
  errorMessage?: string;
  variant?: 'default' | 'filter';
  labelClassName?: string;
}

const SearchableSelect: React.FC<ISearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  label,
  disabled = false,
  className = '',
  containerClassName = '',
  maxHeight = 200,
  icon: Icon,
  searchIcon: SearchIcon = Search,
  error = false,
  errorMessage,
  variant = 'default',
  labelClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updateDropdownPosition = useCallback(() => {
    if (!selectRef.current) {
      return;
    }
    const rect = selectRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = Math.min(maxHeight, filteredOptions.length * 40 + 16);
    const opensUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: opensUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [maxHeight, filteredOptions.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        selectRef.current &&
        !selectRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position dropdown via portal when open
  useEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }
    updateDropdownPosition();
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset highlighted index when dropdown closes or search term changes
  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
    } else {
      // Reset to -1 when search term changes
      setHighlightedIndex(-1);
    }
  }, [isOpen, searchTerm]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (
      highlightedIndex >= 0 &&
      highlightedIndex < filteredOptions.length &&
      optionRefs.current[highlightedIndex]
    ) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex, filteredOptions.length]);

  // Handle option selection
  const handleSelect = (option: ISelectOption) => {
    if (option.disabled) {
      return;
    }

    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  // Handle input change for search
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Helper function to find next enabled option
  const findNextEnabledOption = (currentIndex: number, direction: 'up' | 'down'): number => {
    const startIndex = direction === 'down' ? currentIndex + 1 : currentIndex - 1;
    const endIndex = direction === 'down' ? filteredOptions.length : -1;
    const step = direction === 'down' ? 1 : -1;

    for (let i = startIndex; i !== endIndex; i += step) {
      if (i >= 0 && i < filteredOptions.length && !filteredOptions[i].disabled) {
        return i;
      }
    }
    return currentIndex; // Return current if no enabled option found
  };

  // Handle keyboard navigation in search input
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        if (highlightedIndex < 0) {
          // Start from first enabled option
          const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
          setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
        } else {
          // Continue navigation from current position
          const nextIndex = findNextEnabledOption(highlightedIndex, 'down');
          if (nextIndex === highlightedIndex && highlightedIndex < filteredOptions.length - 1) {
            // If we're at the end, wrap to first enabled option
            const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
            setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
          } else {
            setHighlightedIndex(nextIndex);
          }
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        if (highlightedIndex < 0) {
          // Start from last enabled option
          for (let i = filteredOptions.length - 1; i >= 0; i--) {
            if (!filteredOptions[i].disabled) {
              setHighlightedIndex(i);
              return;
            }
          }
        } else {
          // Continue navigation from current position
          const prevIndex = findNextEnabledOption(highlightedIndex, 'up');
          if (prevIndex === highlightedIndex && highlightedIndex > 0) {
            // If we're at the start, wrap to last enabled option
            for (let i = filteredOptions.length - 1; i >= 0; i--) {
              if (!filteredOptions[i].disabled) {
                setHighlightedIndex(i);
                return;
              }
            }
          } else {
            setHighlightedIndex(prevIndex);
          }
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (
        highlightedIndex >= 0 &&
        highlightedIndex < filteredOptions.length &&
        filteredOptions[highlightedIndex] &&
        !filteredOptions[highlightedIndex].disabled
      ) {
        handleSelect(filteredOptions[highlightedIndex]);
      } else if (filteredOptions.length === 1 && !filteredOptions[0].disabled) {
        // If only one option after filtering, select it
        handleSelect(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        // Select the highlighted option
        handleSelect(filteredOptions[highlightedIndex]);
      } else {
        // Open dropdown
        toggleDropdown();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        // Open dropdown and highlight first enabled option
        setIsOpen(true);
        const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
        setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
      } else {
        // Move to next enabled option
        const nextIndex = findNextEnabledOption(highlightedIndex, 'down');
        if (nextIndex === highlightedIndex && highlightedIndex < filteredOptions.length - 1) {
          // If we're at the end, try to find any enabled option
          const firstEnabledIndex = filteredOptions.findIndex((opt) => !opt.disabled);
          setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
        } else {
          setHighlightedIndex(nextIndex);
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen) {
        // Move to previous enabled option
        const prevIndex = findNextEnabledOption(highlightedIndex, 'up');
        if (prevIndex === highlightedIndex && highlightedIndex > 0) {
          // If we're at the start, try to find last enabled option
          for (let i = filteredOptions.length - 1; i >= 0; i--) {
            if (!filteredOptions[i].disabled) {
              setHighlightedIndex(i);
              return;
            }
          }
        } else {
          setHighlightedIndex(prevIndex);
        }
      }
    }
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const isFilterVariant = variant === 'filter';
  const resolvedLabelClassName =
    labelClassName ??
    (isFilterVariant
      ? 'block text-sm font-medium text-gray-700 mb-1.5'
      : 'block text-base font-semibold text-gray-700 mb-2');
  const textSizeClass = isFilterVariant ? 'text-sm' : '';

  return (
    <div className={`relative ${containerClassName}`}>
      {label && <label className={resolvedLabelClassName}>{label}</label>}

      <div className='relative' ref={selectRef}>
        {/* Custom Select Button */}
        <div
          role='button'
          tabIndex={disabled ? -1 : 0}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={clsx(
            'w-full py-2 bg-white cursor-pointer transition-all duration-200 flex items-center justify-between min-w-0 overflow-hidden focus:outline-none focus:ring-2 focus:ring-theme-500',
            isFilterVariant
              ? [
                  Icon ? 'pl-10' : 'px-3',
                  'pr-8 text-sm border border-gray-300 rounded-md shadow-none text-gray-900 focus:border-theme-500',
                  error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : null,
                  disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400',
                  isOpen ? 'border-theme-500 ring-2 ring-theme-500 ring-opacity-20' : null,
                ]
              : [
                  Icon ? 'pl-12' : 'pl-4',
                  'pr-10 border-2 rounded-xl shadow-sm dark:bg-[#374151] focus:border-transparent',
                  error
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-200',
                  disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-300',
                  isOpen ? 'border-theme-500 ring-2 ring-theme-500 ring-opacity-20' : null,
                ],
            className
          )}
        >
          {/* Icon */}
          {Icon && (
            <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
              <Icon className='h-5 w-5 text-gray-400' />
            </div>
          )}

          {/* Selected text or search input */}
          {isOpen ? (
            <div className='flex-1 flex items-center min-w-0'>
              <SearchIcon className='h-4 w-4 text-gray-400 mr-2 shrink-0' />
              <input
                ref={inputRef}
                type='text'
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                placeholder={selectedOption ? selectedOption.label : placeholder}
                className={clsx(
                  'flex-1 min-w-0 text-left bg-transparent outline-none text-gray-900 placeholder-gray-500 w-full',
                  textSizeClass
                )}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <span
              title={selectedOption ? selectedOption.label : placeholder}
              className={clsx(
                'flex-1 min-w-0 text-left truncate',
                textSizeClass,
                selectedOption ? 'text-gray-900' : 'text-gray-500'
              )}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          )}

          {/* Dropdown arrow */}
          <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
            <ChevronDown
              className={clsx(
                'text-gray-400 transition-transform duration-200',
                isFilterVariant ? 'h-4 w-4' : 'h-5 w-5',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </div>

        {/* Custom Dropdown — portal avoids clipping inside scroll containers */}
        {isOpen &&
          dropdownPosition &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={dropdownRef}
              className={clsx(
                'fixed bg-white shadow-lg z-[9999] overflow-hidden',
                isFilterVariant
                  ? 'border border-gray-300 rounded-md'
                  : 'border-2 border-gray-200 rounded-xl',
                error && 'border-red-300'
              )}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                maxHeight: `${maxHeight}px`,
              }}
            >
              <div
                className='overflow-y-auto custom-scrollbar'
                style={{ maxHeight: `${maxHeight}px` }}
              >
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <div
                      key={`${option.value || 'option'}-${index}`}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      role='option'
                      tabIndex={0}
                      aria-selected={highlightedIndex === index}
                      onClick={() => handleSelect(option)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelect(option);
                        }
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      title={option.label}
                      className={`
                      px-4 py-2 min-w-0 cursor-pointer transition-colors duration-150 border-b border-gray-50 dark:border-gray-500 last:border-b-0
                      focus:outline-none focus:bg-theme-50 focus:dark:bg-gray-700 break-words
                      ${
                        option.disabled
                          ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                          : 'text-gray-900 hover:bg-theme-50'
                      }
                      ${value === option.value ? 'bg-theme-50 text-theme-700 font-medium' : ''}
                      ${highlightedIndex === index && !option.disabled ? 'bg-theme-100' : ''}
                    `}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  <div className='px-4 py-6 text-center text-gray-500 text-sm'>
                    {searchTerm ? 'No options found' : 'No options available'}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Error Message */}
      {error && errorMessage && (
        <p className='mt-2 text-sm text-red-600 font-medium'>{errorMessage}</p>
      )}

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
        }

        .custom-scrollbar::-webkit-scrollbar-corner {
          background: #f8fafc;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-corner {
          background: #374151;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
          border-radius: 3px;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }

        /* Firefox */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f8fafc;
        }

        .dark .custom-scrollbar {
          scrollbar-color: #4b5563 #374151;
        }
      `}</style>
    </div>
  );
};

export default SearchableSelect;
